import { inject, Injectable } from "@angular/core";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { applyFloorplanGroupMigrationWithExternalFloorplans } from "@domain/project/project-deserializer";
import { ProjectFloorplanData, ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import { ScopedProject } from "@domain/project/project-guards";
import { SyncState } from "@domain/project/project-image.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { from, map, Observable, of, switchMap, tap, toArray } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ProjectFloorplanMigrationService {
  private projectFloorplanService = inject(ProjectFloorplanService);

  migrateOldProjectFloorplans(project: ScopedProject): Observable<boolean> {
    const floorplans: any[] = (project as any).floorplans ?? [];

    if (floorplans.length === 0) {
      return of(false);
    }

    return from(floorplans).pipe(
      map((floorplan) => ({
        floorplan: floorplan,
        projectId: project.id,
        syncState: SyncState.NEW,
      })),
      toArray(),
      switchMap((allFloorplanData: ProjectFloorplanData[]) => {
        if (allFloorplanData.length === 0) {
          return of(false);
        }
        return this.projectFloorplanService.saveFloorplansToIndexedDB(allFloorplanData).pipe(
          tap(() => delete (project as any).floorplans),
          map(() => true),
        );
      }),
    );
  }

  /**
   * Migrates old floorplan-level groups to project-level groups for projects where floorplans
   * have already been extracted from the project payload into the separate floorplan store.
   *
   * This handles the case where the floorplan extraction happened before the group migration,
   * leaving old `floorplan.groups[]` data in the separate floorplan store.
   */
  migrateExtractedFloorplanGroups(project: ScopedProject): Observable<boolean> {
    return this.projectFloorplanService.getFloorplansFromIndexedDB(project.id).pipe(
      switchMap((floorplanData) => {
        const rawFloorplains = floorplanData.map((d) => instanceToPlain(d.floorplan) as Record<string, unknown>);
        const scope: BusinessScope = (project as any).businessScope ?? BusinessScope.FGDS;

        const result = applyFloorplanGroupMigrationWithExternalFloorplans(
          instanceToPlain(project) as Record<string, unknown>,
          rawFloorplains,
          scope,
        );

        if (!result) {
          return of(false);
        }

        // Update project groups
        const migratedGroups = (result.projectPlain["groups"] as Record<string, unknown>[]) ?? [];
        const groupInstances = migratedGroups.map((g) => plainToInstance(ProductItemGroup, g));
        (project as any)._groups = groupInstances;

        // Backfill productItemReferences from migrated group references (flat index for duplicate-item lookup)
        const allRefs = groupInstances.flatMap((g) => g.references);
        const seen = new Set<string>();
        const uniqueRefs: FloorplanItemReference[] = [];
        for (const ref of allRefs) {
          const key = `${ref.floorplanId}:${ref.floorplanProductItemId}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueRefs.push(ref);
          }
        }
        (project as any)._productItemReferences = uniqueRefs;

        // Update floorplans in IndexedDB with migrated data (groups field removed, products lifted)
        const updatedFloorplanData: ProjectFloorplanData[] = floorplanData.map((d, i) => ({
          ...d,
          floorplan: plainToInstance(Floorplan, result.migratedFloorplains[i] ?? instanceToPlain(d.floorplan)),
        }));

        return this.projectFloorplanService.saveFloorplansToIndexedDB(updatedFloorplanData).pipe(
          tap(() => {
            project.cachedFloorplans = updatedFloorplanData.map((d) => d.floorplan);
          }),
          map(() => true),
        );
      }),
    );
  }
}
